import { lazy } from "react";
import {
  AgreementFormRedirect,
  CriticalPathProposalRedirect,
  CustodialRequestIndexRedirect,
  ImplementRedirect,
} from "../components/app/LegacyRouteRedirect";
import { HOUSEHOLD_PAGE_ACTIONS } from "./permissions";

const authorized = (component, resource, action) => ({
  component,
  permission: { resource, action },
});

const NoMatch = lazy(() => import("../containers/NoMatch"));
const Dashboard = lazy(() => import("../containers/dashboard/Dashboard"));

const Roles = lazy(() => import("../containers/role/Roles"));
const Role = lazy(() => import("../containers/role/Role"));
const NewRole = lazy(() => import("../containers/role/NewRole"));
const EditRole = lazy(() => import("../containers/role/EditRole"));

const Users = lazy(() => import("../containers/user/Users"));
const Profile = lazy(() => import("../containers/user/Profile"));
const NewUser = lazy(() => import("../containers/user/NewUser"));
const EditUser = lazy(() => import("../containers/user/EditUser"));
const Activation = lazy(() => import("../containers/user/Activation"));
const Impersonating = lazy(() => import("../containers/user/Impersonating"));

const Monitors = lazy(() => import("../containers/monitor/Monitors"));

const Companies = lazy(() => import("../containers/company/Companies"));
const NewCompany = lazy(() => import("../containers/company/NewCompany"));
const Company = lazy(() => import("../containers/company/Company"));
const EditCompany = lazy(() => import("../containers/company/EditCompany"));

const Households = lazy(() => import("../containers/household/Households"));
const NewHousehold = lazy(() => import("../containers/household/NewHousehold"));
const EditHousehold = lazy(() => import("../containers/household/EditHousehold"));

const ImplementationSettings = lazy(() => import("../containers/implementation/ImplementationSettings"));
const ImplementationSettingsExternal = lazy(
  () => import("../containers/implementation/ImplementationSettingsExternal"),
);

const Accounts = lazy(() => import("../containers/account/Accounts"));
const NewAccount = lazy(() => import("../containers/account/NewAccount"));
const NewManualAccount = lazy(() => import("../containers/account/NewManualAccount"));
const EditAccount = lazy(() => import("../containers/account/EditAccount"));
const NewAccounts = lazy(() => import("../containers/account/NewAccounts"));
const NewTamaracAccount = lazy(() => import("../containers/account/NewTamaracAccount"));

const SignIn = lazy(() => import("../containers/auth/SignIn"));
const SignOut = lazy(() => import("../containers/auth/SignOut"));
const NotAuthorized = lazy(() => import("../containers/auth/NotAuthorized"));
const ChangePassword = lazy(() => import("../containers/auth/ChangePassword"));
const ResetPassword = lazy(() => import("../containers/auth/ResetPassword"));
const VerifyEmail = lazy(() => import("../containers/auth/VerifyEmail"));

const Proposal = lazy(() => import("../containers/proposal/Proposal"));
const Proposals = lazy(() => import("../containers/proposal/Proposals"));
const ProposalRequest = lazy(() => import("../containers/proposal/ProposalRequest"));
const EditProposalRequest = lazy(() => import("../containers/proposal/EditProposalRequest"));

const CriticalPaths = lazy(() => import("../containers/criticalpath/CriticalPaths"));
const NewCriticalPath = lazy(() => import("../containers/criticalpath/NewCriticalPath"));
const EditCriticalPath = lazy(() => import("../containers/criticalpath/EditCriticalPath"));
const MonitoringCriticalPath = lazy(() => import("../containers/criticalpath/MonitoringCriticalPath"));

const Models = lazy(() => import("../containers/model/Models"));
const NewModel = lazy(() => import("../containers/model/NewModel"));
const EditModel = lazy(() => import("../containers/model/EditModel"));
const Model = lazy(() => import("../containers/model/Model"));

const Agreement = lazy(() => import("../containers/agreement/Agreement"));
const CustodialForm = lazy(() => import("../containers/custodial-form/CustodialForm"));

const ContactRequest = lazy(() => import("../containers/support/ContactRequest"));

const Monitor = lazy(() => import("../containers/monitor/Monitor"));

const ServiceTeams = lazy(() => import("../containers/service-team/ServiceTeams"));
const NewServiceTeam = lazy(() => import("../containers/service-team/NewServiceTeam"));
const EditServiceTeam = lazy(() => import("../containers/service-team/EditServiceTeam"));

const TermsConditions = lazy(() => import("../containers/page/TermsConditions"));
const AdvisorConsultingAgreement = lazy(() => import("../containers/page/AdvisorConsultingAgreement"));

const SecurityRestrictions = lazy(() => import("../containers/security-restriction/SecurityRestrictions"));

const BillingDefinitions = lazy(() => import("../containers/billing-definition/BillingDefinitions"));
const BillingGroups = lazy(() => import("../containers/billing-group/BillingGroups"));

const AutomatedInvesting = lazy(() => import("../containers/account-ar/AutomatedInvesting"));

const CashReserves = lazy(() => import("../containers/cash-reserve/CashReserves"));

const ZendeskAuth = lazy(() => import("../containers/zendesk/ZendeskAuth"));

const Clients = lazy(() => import("../containers/client/Clients"));
const NewClient = lazy(() => import("../containers/client/NewClient"));
const EditClient = lazy(() => import("../containers/client/EditClient"));

const CustodialRequests = lazy(() => import("../containers/custodial-request/CustodialRequests"));
const CustodialRequestNew = lazy(() => import("../containers/custodial-request/CustodialRequestNew"));
const CustodialRequest = lazy(() => import("../containers/custodial-request/CustodialRequest"));

const TradeRequests = lazy(() => import("../containers/trade-request/TradeRequests"));
const TradeRequest = lazy(() => import("../containers/trade-request/TradeRequest"));

const ProposalInventory = lazy(() => import("../containers/proposal-inventory/ProposalInventory"));

const CustodialForms = lazy(() => import("../containers/custodial-form/CustodialForms"));
const NewCustodialForm = lazy(() => import("../containers/custodial-form/NewCustodialForm"));
const EditCustodialForm = lazy(() => import("../containers/custodial-form/EditCustodialForm"));

const TamaracTransactions = lazy(() => import("../containers/tamarac-transaction/TamaracTransactions"));

const ModelUpdates = lazy(() => import("../containers/model-update/ModelUpdates"));
const ModelUpdateMappings = lazy(() => import("../containers/model-update/ModelUpdateMappings"));
const ModelUpdateBatchDetail = lazy(() => import("../containers/model-update/ModelUpdateBatchDetail"));
const ModelUpdateRunDetail = lazy(() => import("../containers/model-update/ModelUpdateRunDetail"));

const Watchers = lazy(() => import("../containers/watcher/Watchers"));
const NewWatcher = lazy(() => import("../containers/watcher/NewWatcher"));
const EditWatcher = lazy(() => import("../containers/watcher/EditWatcher"));

const NewWorker = lazy(() => import("../containers/worker/NewWorker"));

const Leads = lazy(() => import("../containers/lead/Leads"));

const IncomeBuilders = lazy(() => import("../containers/income-builder/IncomeBuilders"));
const EditIncomeBuilder = lazy(() => import("../containers/income-builder/EditIncomeBuilder"));

const Rebalancing = lazy(() => import("../containers/rebalancing/Rebalancing"));

const Reports = lazy(() => import("../containers/report/Reports"));
const NewReport = lazy(() => import("../containers/report/NewReport"));
const EditReport = lazy(() => import("../containers/report/EditReport"));
const UnifiedReports = lazy(() => import("../containers/report/UnifiedReports"));
const NewUnifiedReport = lazy(() => import("../containers/report/NewUnifiedReport"));
const EditUnifiedReport = lazy(() => import("../containers/report/EditUnifiedReport"));
const ProjectedIncome = lazy(() => import("../containers/report/ProjectedIncome"));
const ProjectedIncomeAudit = lazy(() => import("../containers/report/ProjectedIncomeAudit"));
const ProjectedIncomeView = lazy(() => import("../containers/projected-income/ProjectedIncomeView"));

const Banners = lazy(() => import("../containers/banner/Banners"));
const NewBanner = lazy(() => import("../containers/banner/NewBanner"));
const EditBanner = lazy(() => import("../containers/banner/EditBanner"));

const EducationalMaterials = lazy(() => import("../containers/educational-material/EducationalMaterials"));
const NewEducationMaterial = lazy(() => import("../containers/educational-material/NewEducationMaterial"));
const EditEducationMaterial = lazy(() => import("../containers/educational-material/EditEducationMaterial"));

const AccountTypes = lazy(() => import("../containers/account-type/AccountTypes"));

const ADVReports = lazy(() => import("../containers/adv-report/ADVReports"));
const BillingFeeFiles = lazy(() => import("../containers/billing-fee-file/BillingFeeFiles"));

const Templates = lazy(() => import("../containers/template/Templates"));
const NewTemplate = lazy(() => import("../containers/template/NewTemplate"));
const EditTemplate = lazy(() => import("../containers/template/EditTemplate"));

const TemplateRoutes = lazy(() => import("../containers/template-route/TemplateRoutes"));
const NewTemplateRoute = lazy(() => import("../containers/template-route/NewTemplateRoute"));
const EditTemplateRoute = lazy(() => import("../containers/template-route/EditTemplateRoute"));

export const unauthenticatedRoutes = [
  { exact: true, path: "/signin", component: SignIn },
  { exact: true, path: "/change-password", component: ChangePassword },
  { exact: true, path: "/reset-password", component: ResetPassword },
  { exact: true, path: "/not-authorized", component: NotAuthorized },
  { exact: true, path: "/activate", component: Activation },
  { exact: true, path: "/verify-email", component: VerifyEmail },
  { exact: true, path: "/terms-and-conditions", component: TermsConditions },
  {
    exact: true,
    path: "/advisor-consulting-agreement",
    component: AdvisorConsultingAgreement,
  },
  { exact: true, path: "/proposal/:id/view", component: Proposal },
  { exact: true, path: "/model/:id/view", component: Model },
  {
    exact: true,
    path: "/critical-path-monitoring/:id/view",
    component: MonitoringCriticalPath,
  },
];

export const authenticatedRoutes = [
  { exact: true, path: "/", component: Dashboard },
  { exact: true, path: "/profile", component: Profile },
  { exact: true, path: "/user/:id/edit", component: EditUser },
  { exact: true, path: "/signout", component: SignOut },
  { exact: true, path: "/agreement", component: Agreement },
  { exact: true, path: "/agreement-form", component: AgreementFormRedirect },
  { exact: true, path: "/custodial-forms", ...authorized(CustodialForm, "custodial forms", "list") },
  { exact: true, path: "/onboard-implement", component: ImplementRedirect },
  { exact: true, path: "/custodial-request", component: CustodialRequestIndexRedirect },
  { exact: true, path: "/contact-us", component: ContactRequest },
  { exact: true, path: "/monitoring", component: Monitors },
  { exact: true, path: "/monitoring/:id/", component: Monitor },
  {
    exact: true,
    path: "/accounts",
    ...authorized(Accounts, "accounts", "list"),
  },
  {
    exact: true,
    path: "/companies",
    ...authorized(Companies, "companies", "list"),
  },
  {
    exact: true,
    path: "/households",
    ...authorized(Households, "households", "list"),
  },
  {
    exact: true,
    path: "/implement",
    component: ImplementRedirect,
  },
  {
    exact: true,
    path: "/models",
    ...authorized(Models, "models", "list"),
  },
  {
    exact: true,
    path: "/proposals",
    ...authorized(Proposals, "proposals", "list"),
  },
  { exact: true, path: "/roles", ...authorized(Roles, "roles", "list") },
  { exact: true, path: "/users", ...authorized(Users, "users", "list") },
  {
    exact: true,
    path: "/service-teams",
    ...authorized(ServiceTeams, "service teams", "list"),
  },
  {
    exact: true,
    path: "/critical-paths",
    ...authorized(CriticalPaths, "critical-paths", "list"),
  },
  {
    exact: true,
    path: "/critical-path-proposal",
    component: CriticalPathProposalRedirect,
  },
  {
    exact: true,
    path: "/security-restrictions",
    ...authorized(SecurityRestrictions, "households", "list"),
  },
  {
    exact: true,
    path: "/billing-definitions",
    ...authorized(BillingDefinitions, "billing-definitions", "list"),
  },
  {
    exact: true,
    path: "/billing-groups",
    ...authorized(BillingGroups, "billing-groups", "list"),
  },
  {
    exact: true,
    path: "/automated-investing",
    ...authorized(AutomatedInvesting, "automated-investing", "list"),
  },
  {
    exact: true,
    path: "/cash-reserves",
    ...authorized(CashReserves, "cash-reserves", "list"),
  },
  {
    exact: true,
    path: "/proposal/new",
    ...authorized(ProposalRequest, "proposals", "new"),
  },
  {
    exact: true,
    path: "/account/new-accounts",
    ...authorized(NewAccounts, "accounts", "new tamarac accounts"),
  },
  {
    exact: true,
    path: "/account/new/manual",
    ...authorized(NewManualAccount, "accounts", "manual account"),
  },
  {
    exact: true,
    path: "/account/new",
    ...authorized(NewAccount, "accounts", "new"),
  },
  {
    exact: true,
    path: "/company/new",
    ...authorized(NewCompany, "companies", "new"),
  },
  {
    exact: true,
    path: "/household/new",
    ...authorized(NewHousehold, "households", "new"),
  },
  {
    exact: true,
    path: "/model/new",
    ...authorized(NewModel, "models", "new"),
  },
  {
    exact: true,
    path: "/role/new",
    ...authorized(NewRole, "roles", "new"),
  },
  {
    exact: true,
    path: "/user/new",
    ...authorized(NewUser, "users", "new"),
  },
  {
    exact: true,
    path: "/service-teams/new",
    ...authorized(NewServiceTeam, "service teams", "new"),
  },
  {
    exact: true,
    path: "/critical-path/new",
    ...authorized(NewCriticalPath, "critical-paths", "new"),
  },
  {
    exact: true,
    path: "/account/:id/new-account",
    ...authorized(NewTamaracAccount, "accounts", "new tamarac accounts"),
  },
  {
    exact: true,
    path: "/account/:id/edit",
    ...authorized(EditAccount, "accounts", "edit"),
  },
  {
    exact: true,
    path: "/company/:id/edit",
    ...authorized(EditCompany, "companies", "edit"),
  },
  {
    exact: true,
    path: "/household/:id/edit",
    ...authorized(EditHousehold, "households", HOUSEHOLD_PAGE_ACTIONS),
  },
  {
    exact: true,
    path: "/proposal/:id/account-settings",
    component: ImplementationSettings,
  },
  {
    exact: true,
    path: "/proposal/:id/account-settings-external",
    component: ImplementationSettingsExternal,
  },
  {
    exact: true,
    path: "/model/:id/edit",
    ...authorized(EditModel, "models", "edit"),
  },
  {
    exact: true,
    path: "/role/:id/edit",
    ...authorized(EditRole, "roles", "edit"),
  },
  {
    exact: true,
    path: "/service-teams/:id/edit",
    ...authorized(EditServiceTeam, "service teams", "edit"),
  },
  {
    exact: true,
    path: "/proposal/:id/edit",
    ...authorized(EditProposalRequest, "proposals", "edit"),
  },
  {
    exact: true,
    path: "/critical-path/:id/edit",
    ...authorized(EditCriticalPath, "critical-paths", "edit"),
  },
  {
    exact: true,
    path: "/critical-path/:id/monitoring",
    ...authorized(MonitoringCriticalPath, "critical-paths", "monitoring"),
  },
  {
    exact: true,
    path: "/company/:id",
    ...authorized(Company, "companies", "show"),
  },
  {
    exact: true,
    path: "/role/:id",
    ...authorized(Role, "roles", "show"),
  },
  { exact: true, path: "/proposal/:id", component: Proposal },
  { exact: true, path: "/model/:id", component: Model },
  { exact: true, path: "/zendesk/auth", component: ZendeskAuth },
  { exact: true, path: "/clients", component: Clients },
  { exact: true, path: "/client/new", component: NewClient },
  { exact: true, path: "/client/:id/edit", component: EditClient },
  {
    exact: true,
    path: "/custodial-requests",
    ...authorized(CustodialRequests, "custodial requests", "list"),
  },
  {
    exact: true,
    path: "/custodial-request/new",
    ...authorized(CustodialRequestNew, "custodial requests", "new"),
  },
  {
    exact: true,
    path: "/custodial-request/:id/edit",
    ...authorized(CustodialRequest, "custodial requests", "edit"),
  },
  {
    exact: true,
    path: "/trading-requests",
    ...authorized(TradeRequests, "trading requests", "list"),
  },
  {
    exact: true,
    path: "/trade-request/:id",
    ...authorized(TradeRequest, "trading requests", "view"),
  },
  {
    exact: true,
    path: "/proposal-inventory",
    ...authorized(ProposalInventory, "proposal inventory", "list"),
  },
  {
    exact: true,
    path: "/form-manager",
    ...authorized(CustodialForms, "custodial forms", "list"),
  },
  {
    exact: true,
    path: "/form-manager/new",
    ...authorized(NewCustodialForm, "custodial forms", "new"),
  },
  {
    exact: true,
    path: "/form-manager/:id/edit",
    ...authorized(EditCustodialForm, "custodial forms", "edit"),
  },
  {
    exact: true,
    path: "/tamarac-uploads",
    ...authorized(TamaracTransactions, "tamarac uploads", "list"),
  },
  {
    exact: true,
    path: "/watchers",
    ...authorized(Watchers, "watchers", "list"),
  },
  {
    exact: true,
    path: "/watcher/new",
    ...authorized(NewWatcher, "watchers", "new"),
  },
  {
    exact: true,
    path: "/watcher/:id/edit",
    ...authorized(EditWatcher, "watchers", "edit"),
  },
  {
    exact: true,
    path: "/worker/new",
    ...authorized(NewWorker, "workers", "new"),
  },
  { exact: true, path: "/rebalancing", ...authorized(Rebalancing, "rebalancing", "run") },
  { exact: true, path: "/impersonating", component: Impersonating },
  { exact: true, path: "/leads", ...authorized(Leads, "internal", "visible") },
  {
    exact: true,
    path: "/income-builders",
    ...authorized(IncomeBuilders, "income builders", "list"),
  },
  {
    exact: true,
    path: "/income-builder/:id/edit",
    ...authorized(EditIncomeBuilder, "income builders", "edit"),
  },
  {
    exact: true,
    path: "/tamarac-imports/new",
    ...authorized(NewReport, "tamarac imports", "new"),
  },
  {
    exact: true,
    path: "/tamarac-imports/:id/edit",
    ...authorized(EditReport, "tamarac imports", "edit"),
  },
  {
    exact: true,
    path: "/tamarac-imports",
    ...authorized(Reports, "tamarac imports", "list"),
  },
  {
    exact: true,
    path: "/unified-reports/new",
    ...authorized(NewUnifiedReport, "tamarac imports", "unified reports"),
  },
  {
    exact: true,
    path: "/unified-reports/:id/edit",
    ...authorized(EditUnifiedReport, "tamarac imports", "unified reports"),
  },
  {
    exact: true,
    path: "/unified-reports",
    ...authorized(UnifiedReports, "tamarac imports", "unified reports"),
  },
  {
    exact: true,
    path: "/projected-income",
    ...authorized(ProjectedIncomeView, "reports", "projected income view"),
  },
  {
    exact: true,
    path: "/reports/projected-income/:id",
    ...authorized(ProjectedIncome, "reports", "projected income"),
  },
  {
    exact: true,
    path: "/reports/projected-income-audit",
    ...authorized(ProjectedIncomeAudit, "reports", "projected income"),
  },
  {
    exact: true,
    path: "/banners/new",
    ...authorized(NewBanner, "banners", "new"),
  },
  {
    exact: true,
    path: "/banners/:id/edit",
    ...authorized(EditBanner, "banners", "edit"),
  },
  {
    exact: true,
    path: "/banners",
    ...authorized(Banners, "banners", "list"),
  },
  {
    exact: true,
    path: "/educational-materials/new",
    ...authorized(NewEducationMaterial, "educational materials", "new"),
  },
  {
    exact: true,
    path: "/educational-materials/:id/edit",
    ...authorized(EditEducationMaterial, "educational materials", "edit"),
  },
  {
    exact: true,
    path: "/educational-materials",
    ...authorized(EducationalMaterials, "educational materials", "list"),
  },
  { exact: true, path: "/account-types", ...authorized(AccountTypes, "internal", "visible") },
  { exact: true, path: "/adv-reports", ...authorized(ADVReports, "adv reports", "list") },
  { exact: true, path: "/billing-calculations", ...authorized(BillingFeeFiles, "internal", "visible") },
  {
    exact: true,
    path: "/model-updates",
    ...authorized(ModelUpdates, "model updates", "view"),
  },
  {
    exact: true,
    path: "/model-updates/mappings",
    ...authorized(ModelUpdateMappings, "model updates", "view"),
  },
  {
    exact: true,
    path: "/model-updates/runs/:id",
    ...authorized(ModelUpdateRunDetail, "model updates", "view"),
  },
  {
    exact: true,
    path: "/model-updates/:id",
    ...authorized(ModelUpdateBatchDetail, "model updates", "view"),
  },
  {
    exact: true,
    path: "/templates/new",
    ...authorized(NewTemplate, "templates", "new"),
  },
  {
    exact: true,
    path: "/templates/:id/edit",
    ...authorized(EditTemplate, "templates", "edit"),
  },
  {
    exact: true,
    path: "/templates",
    ...authorized(Templates, "templates", "list"),
  },
  {
    exact: true,
    path: "/routes/new",
    ...authorized(NewTemplateRoute, "routes", "new"),
  },
  {
    exact: true,
    path: "/routes/:id/edit",
    ...authorized(EditTemplateRoute, "routes", "edit"),
  },
  {
    exact: true,
    path: "/routes",
    ...authorized(TemplateRoutes, "routes", "list"),
  },
  { component: NoMatch },
];
